import PropTypes from 'prop-types';
import { hydrateRoot } from 'react-dom/client';
import { createOperationDescriptor, getRequest } from 'relay-runtime';
import serverVars from 'server-vars';
import { ReactRelayContext } from 'react-relay/legacy.js';
import { Provider } from 'react-redux';
import { IntlProvider } from 'dibs-react-intl';
import { ServerVarsContextProvider } from '../global/ServerVarsContext/ServerVarsContext.tsx';
import { QP_SEARCH_QUERY } from '../constants/queryParamConstants.ts';
import { Error500Layout } from 'dibs-buyer-layout/exports/error500.tsx';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import { SocketContextProvider } from 'dibs-subscriptions-utils/exports/SocketContext.ts';
import { getIsBotOnClient } from 'dibs-buyer-layout/exports/botHelpers.ts';

function PrepareRelayContextProvider(props) {
    const { environment, query, variables, Component, ...otherProps } = props;
    const operation = createOperationDescriptor(getRequest(query), variables);
    environment.retain(operation);

    const snapshot = environment.lookup(operation.fragment);
    const { data } = snapshot;

    const renderProps = {
        ...variables,
        ...otherProps,
        ...data,
    };

    return (
        <ReactRelayContext
            value={{
                environment: environment,
                variables: variables || {},
            }}
        >
            <Component {...renderProps} />
        </ReactRelayContext>
    );
}

PrepareRelayContextProvider.propTypes = {
    environment: PropTypes.object.isRequired,
    query: PropTypes.object.isRequired,
    variables: PropTypes.object.isRequired,
    Component: PropTypes.elementType.isRequired,
};

export function renderEntryComponent(options) {
    const { containerId, store, socketListeners, ...otherProps } = options;
    const searchParams = new URLSearchParams(location.search);

    hydrateRoot(
        document.getElementById(containerId),
        <IntlProvider locale={serverVars.get('locale')} messages={serverVars.get('messages')}>
            <ErrorBoundary fallback={({ error }) => <Error500Layout error={error} />}>
                <ServerVarsContextProvider
                    isBot={getIsBotOnClient()}
                    isMobile={serverVars.get('settings.isMobile')}
                    isTablet={serverVars.get('settings.isTablet')}
                    isDesktop={serverVars.get('settings.isDesktop')}
                    areDisplayAdsEnabled={serverVars.get('areDisplayAdsEnabled')}
                    clientConf={serverVars.get('config')}
                    localeOrigin={serverVars.get('localeOrigin')}
                    originalUrl={serverVars.get('originalUrl')}
                    isSearchQueryApplied={!!searchParams.get(QP_SEARCH_QUERY)}
                    serverCurrency={serverVars.get('serverCurrency')}
                    isRemoveCountsVariant={serverVars.get('isRemoveCountsVariant')}
                    isQuickFiltersRedesignVariant={serverVars.get('isQuickFiltersRedesignVariant')}
                    isDriveSavedSearchAdoptionVariant={serverVars.get(
                        'isDriveSavedSearchAdoptionVariant'
                    )}
                    isTradeQuickFiltersVariant={serverVars.get('isTradeQuickFiltersVariant')}
                    isPdpMwLayoutShiftFixVariant={serverVars.get('isPdpMwLayoutShiftFixVariant')}
                    isTableSizeFilterVariant={serverVars.get('isTableSizeFilterVariant')}
                    urgencySignalsMwVariant={serverVars.get('urgencySignalsMwVariant')}
                    isContentVisibilitySbVariant={serverVars.get('isContentVisibilitySbVariant')}
                    isShipsFromSBTileVariant={serverVars.get('isShipsFromSBTileVariant')}
                    isPdpEstimateSimilarSoldTestVariant={serverVars.get(
                        'isPdpEstimateSimilarSoldTestVariant'
                    )}
                    isPdpSimilarSoldItemsModuleVariant={serverVars.get(
                        'isPdpSimilarSoldItemsModuleVariant'
                    )}
                    isItemFavoritingFeb2026Variant={serverVars.get(
                        'isItemFavoritingFeb2026Variant'
                    )}
                    isItemFavoritingFeb2026Control={serverVars.get(
                        'isItemFavoritingFeb2026Control'
                    )}
                >
                    <SocketContextProvider socketListeners={socketListeners}>
                        <Provider store={store}>
                            <PrepareRelayContextProvider {...otherProps} />
                        </Provider>
                    </SocketContextProvider>
                </ServerVarsContextProvider>
            </ErrorBoundary>
        </IntlProvider>
    );
}
